/**
 * Hybrid Diff Viewer Component
 *
 * Shows side-by-side comparison with a toggle between two modes:
 * 1. Rich HTML mode: Tables, lists, formatting preserved (no diff highlighting)
 * 2. Plain Text mode: Accurate inline diff highlighting (red removed, green added)
 *
 * The toggle allows users to see rich formatting when needed, and switch to
 * accurate diff view when they need to see exact text changes.
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import {
  computeDiff,
  hasActualTextChanges,
  type DiffPart,
} from '../utils/diffUtils';

// ============================================================================
// Types
// ============================================================================

interface HybridDiffViewerProps {
  /** Old/previous version XML content (FDA SPL format) */
  oldContentXml: string | null;
  /** Old/previous version plain text (fallback) */
  oldContentText?: string | null;
  /** New/current version XML content (FDA SPL format) */
  newContentXml: string | null;
  /** New/current version plain text (fallback) */
  newContentText?: string | null;
  /** Old/previous version pre-formatted HTML (draft DOCX tables) */
  oldContentHtml?: string | null;
  /** New/current version pre-formatted HTML (draft DOCX tables) */
  newContentHtml?: string | null;
  /** Drug name for image paths */
  drugName?: string;
  /** Optional className */
  className?: string;
}

type ViewMode = 'rich' | 'diff';

// ============================================================================
// SPL XML Transformation (from SplContentRenderer)
// ============================================================================

// Images are now served via DailyMed URLs injected into content_xml by the backfill script

function processFootnotes(
  content: string,
  tableIndex: number
): { html: string; footnotesHtml: string } {
  const footnotes: Map<string, { number: number; text: string }> = new Map();
  let footnoteCounter = 0;
  const prefix = tableIndex > 0 ? `t${tableIndex}-` : '';

  let html = content;

  html = html.replace(
    /<footnote\s+ID="([^"]+)"([^>]*)>([\s\S]*?)<\/footnote>/gi,
    (_match, id, _attrs, text) => {
      footnoteCounter++;
      const cleanText = text.replace(/<[^>]+>/g, '').trim();
      footnotes.set(id, { number: footnoteCounter, text: cleanText });
      const fnId = `${prefix}${footnoteCounter}`;
      return `<a href="#fn-${fnId}" id="fnref-${fnId}" class="footnote-ref text-indigo-600 hover:underline" data-footnote="${fnId}"><sup>[${footnoteCounter}]</sup></a>`;
    }
  );

  html = html.replace(
    /<footnoteRef\s+IDREF="([^"]+)"([^>]*)\/?>/gi,
    (_match, idref) => {
      const footnote = footnotes.get(idref);
      if (footnote) {
        const fnId = `${prefix}${footnote.number}`;
        return `<a href="#fn-${fnId}" class="footnote-ref text-indigo-600 hover:underline" data-footnote="${fnId}"><sup>[${footnote.number}]</sup></a>`;
      }
      return '';
    }
  );

  let footnotesHtml = '';
  if (footnotes.size > 0) {
    const footnotesList = Array.from(footnotes.entries())
      .sort((a, b) => a[1].number - b[1].number)
      .map(([_id, { number, text }]) => {
        const fnId = `${prefix}${number}`;
        return `<div id="fn-${fnId}" class="footnote-item text-sm text-slate-600 mb-2 pl-6 relative">
          <a href="#fnref-${fnId}" class="absolute left-0 top-0 text-indigo-600 font-medium hover:underline" data-backref="${fnId}">[${number}]</a>
          <span>${text}</span>
        </div>`;
      })
      .join('');
    footnotesHtml = `<div class="footnotes-section mt-3 pt-3 border-t border-slate-200 text-sm">${footnotesList}</div>`;
  }

  return { html, footnotesHtml };
}

function transformSplXmlToHtml(
  xml: string,
): string {
  let html = xml;

  html = html.replace(/xmlns(:[a-z]+)?="[^"]*"/gi, '');

  let tableIndex = 0;
  html = html.replace(
    /<table([^>]*)>([\s\S]*?)<\/table>/gi,
    (_match, attrs, tableContent) => {
      tableIndex++;
      const { html: processedContent, footnotesHtml } = processFootnotes(tableContent, tableIndex);
      return `<table${attrs}>${processedContent}</table>${footnotesHtml}`;
    }
  );

  const { html: finalHtml, footnotesHtml: globalFootnotes } = processFootnotes(html, 0);
  html = finalHtml;
  if (globalFootnotes) {
    html += globalFootnotes;
  }

  html = html.replace(/<paragraph([^>]*)>/gi, '<p class="mb-3 leading-relaxed"$1>');
  html = html.replace(/<\/paragraph>/gi, '</p>');

  html = html.replace(/<content\s+styleCode="underline"([^>]*)>/gi, '<span class="underline"$1>');
  html = html.replace(/<content\s+styleCode="bold"([^>]*)>/gi, '<span class="font-bold"$1>');
  html = html.replace(/<content\s+styleCode="italics"([^>]*)>/gi, '<span class="italic"$1>');
  html = html.replace(/<content\s+styleCode="emphasis"([^>]*)>/gi, '<span class="italic"$1>');
  html = html.replace(/<content([^>]*)>/gi, '<span$1>');
  html = html.replace(/<\/content>/gi, '</span>');

  html = html.replace(/<list[^>]*>[\s\S]*?<\/list>/gi, (match) => {
    return match.replace(/<caption>[^<]*<\/caption>/gi, '');
  });

  html = html.replace(/<list\s+listType="ordered"([^>]*)>/gi, '<!--OL--><ol class="list-decimal ml-6 space-y-1"$1>');
  html = html.replace(/<list\s+listType="unordered"\s+styleCode="[Cc]ircle"([^>]*)>/gi, '<!--UL--><ul class="list-[circle] ml-6 space-y-1"$1>');
  html = html.replace(/<list\s+listType="unordered"\s+styleCode="[Ss]quare"([^>]*)>/gi, '<!--UL--><ul class="list-[square] ml-6 space-y-1"$1>');
  html = html.replace(/<list\s+listType="unordered"([^>]*)>/gi, '<!--UL--><ul class="list-disc ml-6 space-y-1"$1>');
  html = html.replace(/<list([^>]*)>/gi, '<!--UL--><ul class="list-disc ml-6 space-y-1"$1>');

  const listStack: string[] = [];
  let result = '';
  let lastIndex = 0;
  const markerRegex = /<!--(OL|UL)-->|<\/list>/gi;
  let match;

  while ((match = markerRegex.exec(html)) !== null) {
    result += html.substring(lastIndex, match.index);
    if (match[0].startsWith('<!--') && match[1]) {
      listStack.push(match[1]);
      result += match[0];
    } else if (!match[0].startsWith('<!--')) {
      const listType = listStack.pop();
      result += listType === 'OL' ? '</ol>' : '</ul>';
    }
    lastIndex = match.index + match[0].length;
  }
  result += html.substring(lastIndex);
  html = result;

  html = html.replace(/<!--(OL|UL)-->/g, '');

  html = html.replace(/<item([^>]*)>/gi, '<li class="text-slate-700"$1>');
  html = html.replace(/<\/item>/gi, '</li>');

  html = html.replace(/<linkHtml\s+href="#([^"]*)"([^>]*)>/gi, (_match, href, rest) => {
    const sectionMatch = href.match(/(?:S|section-|LINK_)?(\d+(?:\.\d+)?)/i);
    const sectionNum = sectionMatch ? sectionMatch[1] : href;
    return `<a href="#" data-section="${sectionNum}" class="text-indigo-600 hover:underline cursor-pointer"${rest}>`;
  });
  html = html.replace(/<linkHtml\s+href="(https?:\/\/[^"]*)"([^>]*)>/gi, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-indigo-600 hover:underline"$2>');
  html = html.replace(/<\/linkHtml>/gi, '</a>');

  html = html.replace(/<sup([^>]*)>/gi, '<sup class="text-xs"$1>');
  html = html.replace(/<sub([^>]*)>/gi, '<sub class="text-xs"$1>');
  html = html.replace(/<br\s*\/?>/gi, '<br/>');

  // Style any existing <img> tags (DailyMed URLs injected by backfill)
  html = html.replace(
    /<img\s+/gi,
    '<img class="max-w-full h-auto my-4 border border-slate-200 rounded" loading="lazy" onerror="this.style.display=\'none\'" '
  );
  // Remove any remaining <renderMultiMedia> tags not yet backfilled
  html = html.replace(/<renderMultiMedia[^>]*\/?>/gi, '');

  html = html.replace(/<table([^>]*)>/gi, '<table class="w-full border-collapse my-4 text-sm"$1>');
  html = html.replace(/<caption([^>]*)>/gi, '<caption class="text-left font-semibold text-slate-800 mb-2"$1>');
  html = html.replace(/<thead([^>]*)>/gi, '<thead class="bg-slate-100"$1>');
  html = html.replace(/<tfoot([^>]*)>/gi, '<tfoot class="bg-slate-50 text-xs text-slate-600"$1>');
  html = html.replace(/<tbody([^>]*)>/gi, '<tbody$1>');
  html = html.replace(/<tr([^>]*)>/gi, '<tr class="border-b border-slate-200"$1>');
  html = html.replace(/<th([^>]*)>/gi, '<th class="px-3 py-2 text-left font-semibold text-slate-700 border border-slate-300"$1>');
  html = html.replace(/<td([^>]*)>/gi, '<td class="px-3 py-2 text-slate-600 border border-slate-300"$1>');

  html = html.replace(/styleCode="[^"]*Toprule[^"]*"/gi, '');
  html = html.replace(/styleCode="[^"]*Botrule[^"]*"/gi, '');
  html = html.replace(/styleCode="[^"]*Lrule[^"]*"/gi, '');
  html = html.replace(/styleCode="[^"]*Rrule[^"]*"/gi, '');

  html = html.replace(/<text[^>]*>/gi, '<div>');
  html = html.replace(/<\/text>/gi, '</div>');

  html = html.replace(/>\s+</g, '> <');
  html = html.trim();

  return html;
}

const ALLOWED_TAGS = [
  'p', 'br', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u',
  'ul', 'ol', 'li',
  'a',
  'sup', 'sub',
  'table', 'caption', 'thead', 'tfoot', 'tbody', 'tr', 'th', 'td', 'col', 'colgroup',
  'img',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'del', 'ins', // Track changes from DOCX drafts
];

const ALLOWED_ATTR = [
  'class', 'href', 'src', 'alt', 'loading', 'onerror',
  'colspan', 'rowspan', 'width', 'valign', 'align',
  'data-section', 'data-footnote', 'data-backref', 'id',
  'target', 'rel', 'title', 'style',
  'data-author', 'data-date', // Track change metadata
];

// ============================================================================
// View Mode Toggle
// ============================================================================

interface ViewModeToggleProps {
  mode: ViewMode;
  onChange: (mode: ViewMode) => void;
}

const ViewModeToggle: React.FC<ViewModeToggleProps> = ({ mode, onChange }) => {
  return (
    <div className="inline-flex rounded-md shadow-sm" role="group">
      <button
        type="button"
        onClick={() => onChange('rich')}
        className={`px-3 py-1 text-xs font-medium rounded-l-md border ${
          mode === 'rich'
            ? 'bg-indigo-600 text-white border-indigo-600'
            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
        }`}
        title="Show rich formatting (tables, lists) without diff highlighting"
      >
        Rich Format
      </button>
      <button
        type="button"
        onClick={() => onChange('diff')}
        className={`px-3 py-1 text-xs font-medium rounded-r-md border-t border-b border-r ${
          mode === 'diff'
            ? 'bg-indigo-600 text-white border-indigo-600'
            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
        }`}
        title="Show accurate diff highlighting (plain text)"
      >
        Show Changes
      </button>
    </div>
  );
};

// ============================================================================
// Plain Text Diff Renderer
// ============================================================================

interface DiffPartRendererProps {
  parts: DiffPart[];
  side: 'old' | 'new';
}

const DiffPartRenderer: React.FC<DiffPartRendererProps> = ({ parts, side }) => {
  return (
    <>
      {parts.map((part, index) => {
        if (part.type === 'unchanged') {
          return <span key={index}>{part.text}</span>;
        }

        if (side === 'old' && part.type === 'removed') {
          return (
            <span key={index} className="diff-removed">
              {part.text}
            </span>
          );
        }

        if (side === 'new' && part.type === 'added') {
          return (
            <span key={index} className="diff-added">
              {part.text}
            </span>
          );
        }

        return null;
      })}
    </>
  );
};

// ============================================================================
// Rich HTML Panel (No Diff)
// ============================================================================

interface RichHtmlPanelProps {
  html: string;
  title: string;
  variant: 'old' | 'new';
}

const RichHtmlPanel: React.FC<RichHtmlPanelProps> = ({ html, title, variant }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      const footnoteLink = target.closest('a[data-footnote]') as HTMLAnchorElement;
      if (footnoteLink) {
        e.preventDefault();
        const fnId = footnoteLink.dataset['footnote'];
        if (fnId) {
          const footnoteEl = container.querySelector(`#fn-${fnId}`);
          if (footnoteEl) {
            footnoteEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        return;
      }

      const backLink = target.closest('a[data-backref]') as HTMLAnchorElement;
      if (backLink) {
        e.preventDefault();
        const fnId = backLink.dataset['backref'];
        if (fnId) {
          const refEl = container.querySelector(`#fnref-${fnId}`);
          if (refEl) {
            refEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        return;
      }

      const link = target.closest('a[data-section]') as HTMLAnchorElement;
      if (link) {
        e.preventDefault();
      }
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [html]);

  const borderColor = variant === 'old' ? 'border-l-amber-400' : 'border-l-blue-400';

  return (
    <div className={`border rounded-lg overflow-hidden bg-white border-l-4 ${borderColor}`}>
      <div className="px-4 py-2 bg-slate-50 border-b">
        <h4 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">
          {title}
        </h4>
      </div>
      <div className="p-4 max-h-[600px] overflow-y-auto">
        <div
          ref={containerRef}
          className="spl-content prose prose-slate prose-sm max-w-none text-xs leading-relaxed"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Plain Text Diff Panel
// ============================================================================

interface PlainTextDiffPanelProps {
  parts: DiffPart[];
  side: 'old' | 'new';
  title: string;
  changeCount: number;
}

const PlainTextDiffPanel: React.FC<PlainTextDiffPanelProps> = ({
  parts,
  side,
  title,
  changeCount,
}) => {
  const colorClass = side === 'old' ? 'text-red-500' : 'text-green-500';
  const borderColor = side === 'old' ? 'border-l-red-400' : 'border-l-green-400';

  return (
    <div className={`border rounded-lg overflow-hidden bg-white border-l-4 ${borderColor}`}>
      <div className="px-4 py-2 bg-slate-50 border-b">
        <h4 className="font-semibold text-slate-700 text-xs uppercase tracking-wide flex items-center justify-between">
          <span>{title}</span>
          {changeCount > 0 && (
            <span className={`${colorClass} font-normal normal-case`}>
              {changeCount} change{changeCount !== 1 ? 's' : ''}
            </span>
          )}
        </h4>
      </div>
      <div className="p-4 max-h-[600px] overflow-y-auto">
        <div className="text-slate-700 text-xs leading-relaxed whitespace-pre-wrap">
          <DiffPartRenderer parts={parts} side={side} />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const HybridDiffViewer: React.FC<HybridDiffViewerProps> = ({
  oldContentXml,
  oldContentText,
  newContentXml,
  newContentText,
  oldContentHtml,
  newContentHtml,
  drugName = 'Drug',
  className = '',
}) => {
  // Default to diff mode so users see changes by default
  const [viewMode, setViewMode] = useState<ViewMode>('diff');

  const oldContent = oldContentXml || oldContentText || '';
  const newContent = newContentXml || newContentText || '';

  // Check for meaningful changes
  const hasMeaningfulChanges = useMemo(() => {
    return hasActualTextChanges(oldContent, newContent);
  }, [oldContent, newContent]);

  // Compute plain text diff
  const diffResult = useMemo(() => {
    if (!hasMeaningfulChanges) {
      return { oldParts: [], newParts: [], hasChanges: false, removedCount: 0, addedCount: 0 };
    }
    return computeDiff(oldContent, newContent);
  }, [oldContent, newContent, hasMeaningfulChanges]);

  // Transform XML to HTML for rich mode, or use pre-formatted HTML from DOCX
  const { oldHtml, newHtml, hasRichContent } = useMemo(() => {
    // Priority: XML (FDA) > pre-formatted HTML (DOCX drafts)
    let oldHtml = '';
    if (oldContentXml) {
      oldHtml = DOMPurify.sanitize(transformSplXmlToHtml(oldContentXml), {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
      });
    } else if (oldContentHtml) {
      oldHtml = DOMPurify.sanitize(oldContentHtml, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
      });
    }

    let newHtml = '';
    if (newContentXml) {
      newHtml = DOMPurify.sanitize(transformSplXmlToHtml(newContentXml), {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
      });
    } else if (newContentHtml) {
      newHtml = DOMPurify.sanitize(newContentHtml, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
      });
    }

    return {
      oldHtml,
      newHtml,
      hasRichContent: Boolean(oldHtml || newHtml),
    };
  }, [oldContentXml, newContentXml, oldContentHtml, newContentHtml, drugName]);

  // If no meaningful changes
  if (!hasMeaningfulChanges) {
    return (
      <div className={`p-4 bg-slate-50 border border-slate-200 rounded text-slate-600 text-sm ${className}`}>
        <p className="font-medium">No meaningful text changes detected.</p>
        <p className="text-xs mt-1 text-slate-500">
          The differences may be formatting-only (spacing, punctuation) which are not shown.
        </p>
      </div>
    );
  }

  const { oldParts, newParts, removedCount, addedCount } = diffResult;

  return (
    <div className={className}>
      {/* Header with toggle and change counts */}
      <div className="mb-3 flex items-center justify-between gap-4 px-1">
        <div className="flex items-center gap-4 text-xs">
          {removedCount > 0 && (
            <span className="text-red-600 flex items-center gap-1">
              <span className="inline-block w-3 h-3 bg-red-100 border border-red-300 rounded"></span>
              <span><span className="font-semibold">{removedCount}</span> removed</span>
            </span>
          )}
          {addedCount > 0 && (
            <span className="text-green-600 flex items-center gap-1">
              <span className="inline-block w-3 h-3 bg-green-100 border border-green-300 rounded"></span>
              <span><span className="font-semibold">{addedCount}</span> added</span>
            </span>
          )}
        </div>

        {/* Only show toggle if we have rich content */}
        {hasRichContent && (
          <ViewModeToggle mode={viewMode} onChange={setViewMode} />
        )}
      </div>

      {/* Content panels */}
      <div className="grid grid-cols-2 gap-4">
        {viewMode === 'rich' && hasRichContent ? (
          <>
            <RichHtmlPanel
              html={oldHtml}
              title="Previous Version"
              variant="old"
            />
            <RichHtmlPanel
              html={newHtml}
              title="Current Version"
              variant="new"
            />
          </>
        ) : (
          <>
            <PlainTextDiffPanel
              parts={oldParts}
              side="old"
              title="Previous Version"
              changeCount={removedCount}
            />
            <PlainTextDiffPanel
              parts={newParts}
              side="new"
              title="Current Version"
              changeCount={addedCount}
            />
          </>
        )}
      </div>

      {/* Mode hint */}
      {viewMode === 'rich' && hasRichContent && (
        <p className="mt-2 text-xs text-slate-400 text-center">
          Showing rich formatting. Click "Show Changes" to see exact text differences.
        </p>
      )}
    </div>
  );
};

// Re-export hasActualTextChanges for use in VersionComparisonModal
export { hasActualTextChanges } from '../utils/diffUtils';

export default HybridDiffViewer;
